import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { Medicine, Owner } from './localData';
import { invitationLink } from './invitationLink';

export interface CloudContext {
  session: Session;
  householdId: string;
  householdName: string;
  householdRole: 'owner' | 'member' | null;
  members: FamilyMember[];
}

export type FamilyMember = { userId: string; displayName: string; role: 'owner' | 'member'; medicalProfileId?: string };

export type HouseholdInvitation = { id: string; invitedEmail: string; status: 'pending'|'accepted'|'revoked'|'expired'; expiresAt: string; inviteToken?: string };

type MedicineRow = {
  id: string;
  household_id: string;
  name: string;
  active_ingredient: string | null;
  dosage: string | null;
  form: string | null;
  category: string | null;
  notes: string | null;
  photo: string | null;
  usage_tags: string[] | null;
  intake_method: string | null;
  dosing_schedule_text: string | null;
  dosing_schedule_source: string | null;
  dosing_schedule_source_url: string | null;
  medicine_batches: Array<{
    id: string;
    quantity: number;
    unit: string | null;
    expires_on: string | null;
    storage_location: string | null;
    low_stock_threshold: number | null;
  }> | null;
};

function requireClient() {
  if (!supabase) throw new Error('Supabase не настроен');
  return supabase;
}

// Returns null when Supabase's "Confirm email" setting is on — the account
// is created but has no session until the user clicks the confirmation
// link, at which point they sign in normally with signInWithPassword.
export async function signUpWithPassword(email: string, password: string, displayName: string): Promise<Session | null> {
  const client = requireClient();
  const normalizedName = displayName.trim();
  if (!normalizedName) throw new Error('Укажите своё имя');
  const { data, error } = await client.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    // Keep a personal invitation intact through email confirmation.
    options: { emailRedirectTo: window.location.href, data: { full_name: normalizedName } },
  });
  if (error) throw error;
  return data.session;
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const client = requireClient();
  const { data, error } = await client.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error) throw error;
  if (!data.session) throw new Error('Supabase не вернул сессию');
  return data.session;
}

export async function signOut(): Promise<void> {
  const client = requireClient();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

// Sends a "set/reset your password" link. This is also how an account
// created before password auth existed (email/OTP only, no password) gets
// its first password — signUp() silently no-ops on an email that already
// exists, so this is the only way in for those accounts.
export async function requestPasswordReset(email: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
    redirectTo: window.location.origin,
  });
  if (error) throw error;
}

// Called after the user follows the reset link (Supabase Auth fires a
// PASSWORD_RECOVERY session for it — see App.tsx).
export async function setNewPassword(password: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.auth.updateUser({ password });
  if (error) throw error;
}

export async function ensureCloudContext(session: Session): Promise<CloudContext> {
  const client = requireClient();
  const metadataName = session.user.user_metadata?.full_name;
  const displayName = typeof metadataName === 'string' && metadataName.trim()
    ? metadataName.trim()
    : session.user.email?.split('@')[0] || 'Пользователь';

  const { error: profileError } = await client.from('profiles').upsert({
    id: session.user.id,
    display_name: displayName,
  }, { onConflict: 'id' });
  if (profileError) throw profileError;

  const { data: membership, error: membershipError } = await client.rpc('get_my_household');
  if (membershipError) throw membershipError;

  const activeHousehold = membership?.[0] as { household_id: string; household_name: string; member_role: 'owner' | 'member' } | undefined;
  const members = activeHousehold ? await loadFamilyMembers(activeHousehold.household_id) : [];

  // A newly registered person deliberately has no household yet. They choose
  // to create one or accept a personal invitation from «Ещё».
  return {
    session,
    householdId: activeHousehold?.household_id || '',
    householdName: activeHousehold?.household_name || '',
    householdRole: activeHousehold?.member_role || null,
    members,
  };
}

export async function loadFamilyMembers(householdId: string): Promise<FamilyMember[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('household_members')
    .select('user_id, role')
    .eq('household_id', householdId)
    .is('deleted_at', null);
  if (error) throw error;
  const memberUserIds = (data || []).map(row => row.user_id as string);
  const [{ data: accountProfiles, error: accountProfilesError }, { data: medicalProfiles, error: medicalProfilesError }] = await Promise.all([
    client.from('profiles').select('id, display_name').in('id', memberUserIds),
    client.from('medical_profiles').select('id, member_user_id').eq('household_id', householdId),
  ]);
  if (accountProfilesError) throw accountProfilesError;
  if (medicalProfilesError) throw medicalProfilesError;
  const displayNames = new Map((accountProfiles || []).map(profile => [profile.id as string, profile.display_name as string]));
  const medicalProfileIds = new Map((medicalProfiles || []).map(profile => [profile.member_user_id as string, profile.id as string]));
  return (data || []).map(row => {
    const userId = row.user_id as string;
    return { userId, displayName: displayNames.get(userId) || 'Участник семьи', role: row.role as 'owner' | 'member', medicalProfileId: medicalProfileIds.get(userId) };
  }).sort((a, b) => a.displayName.localeCompare(b.displayName, 'ru'));
}

export async function claimLegacyMedicalProfile(legacyOwner: 'anton' | 'anya', memberUserId: string): Promise<void> {
  const client = requireClient();
  const { error } = await client.rpc('claim_legacy_medical_profile', { p_legacy_owner: legacyOwner, p_member_user_id: memberUserId });
  if (error) throw error;
}

export async function ensureMedicalProfile(memberUserId: string): Promise<string> {
  const client = requireClient();
  const { data, error } = await client.rpc('ensure_medical_profile', { p_member_user_id: memberUserId });
  if (error) throw error;
  return data as string;
}

export async function createHouseholdInvitation(householdId: string, email: string): Promise<string> {
  const client = requireClient();
  const invitedEmail = email.trim().toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(invitedEmail)) throw new Error('Укажите корректный email');
  const { data, error } = await client.rpc('create_household_invitation', {
    p_household_id: householdId,
    p_invited_email: invitedEmail,
  });
  if (error) throw error;
  return invitationLink(window.location.origin, data as string);
}

export async function previewHouseholdInvitation(token: string): Promise<{ id: string; householdName: string } | null> {
  const client = requireClient();
  const { data, error } = await client.rpc('preview_household_invitation', { p_token: token });
  if (error) throw error;
  const invitation = data?.[0];
  return invitation ? { id: invitation.invitation_id, householdName: invitation.household_name } : null;
}

export async function acceptHouseholdInvitationLink(token: string): Promise<string> {
  const client = requireClient();
  const { data, error } = await client.rpc('accept_household_invitation_link', { p_token: token });
  if (error) throw error;
  return data as string;
}

export async function renameHousehold(householdId: string, name: string): Promise<void> {
  const client = requireClient();
  const normalizedName = name.trim();
  if (normalizedName.length < 2) throw new Error('Введите название семьи');
  const { error } = await client.from('households').update({ name: normalizedName }).eq('id', householdId);
  if (error) throw error;
}

export async function loadIncomingHouseholdInvitations(): Promise<HouseholdInvitation[]> {
  const client = requireClient();
  const { data, error } = await client.from('household_invitations').select('id, invited_email, status, expires_at').eq('status', 'pending').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({ id: row.id, invitedEmail: row.invited_email, status: row.status, expiresAt: row.expires_at }));
}

export async function acceptHouseholdInvitation(invitationId: string): Promise<string> {
  const client = requireClient();
  const { data, error } = await client.rpc('accept_household_invitation', { p_invitation_id: invitationId });
  if (error) throw error;
  return data as string;
}

export async function createPersonalHousehold(name: string): Promise<string> {
  const client = requireClient();
  const { data, error } = await client.rpc('create_personal_household', { p_name: name.trim() });
  if (error) throw error;
  return data as string;
}

export async function loadCloudMedicines(householdId: string): Promise<Medicine[]> {
  const client = requireClient();
  const { data, error } = await client
    .from('medicines')
    .select('id, household_id, name, active_ingredient, dosage, form, category, notes, photo, usage_tags, intake_method, dosing_schedule_text, dosing_schedule_source, dosing_schedule_source_url, medicine_batches(id, quantity, unit, expires_on, storage_location, low_stock_threshold)')
    .eq('household_id', householdId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;

  return ((data || []) as MedicineRow[]).map(row => {
    const batch = row.medicine_batches?.find(item => item) || null;
    let owner: Owner = 'shared';
    let note = row.notes || '';
    const marker = note.match(/^\[owner:(anton|anya|shared)\]\s*/);
    if (marker) {
      owner = marker[1] as Owner;
      note = note.replace(marker[0], '');
    }
    return {
      id: row.id,
      name: row.name,
      activeIngredient: row.active_ingredient || '',
      dosage: row.dosage || '',
      form: row.form || '',
      category: row.category || '',
      usageSummary: note,
      usageTags: row.usage_tags || [],
      intakeMethod: row.intake_method || undefined,
      dosingScheduleText: row.dosing_schedule_text || undefined,
      dosingScheduleSource: row.dosing_schedule_source || undefined,
      dosingScheduleSourceUrl: row.dosing_schedule_source_url || undefined,
      note,
      photo: row.photo || undefined,
      owner,
      amount: batch?.quantity ?? 0,
      unit: batch?.unit || 'шт.',
      threshold: batch?.low_stock_threshold ?? undefined,
      expiry: batch?.expires_on || '',
      storage: batch?.storage_location || '',
    };
  });
}

export async function createCloudMedicine(householdId: string, userId: string, medicine: Medicine): Promise<Medicine> {
  const client = requireClient();
  const { data: row, error } = await client.from('medicines').insert({
    household_id: householdId,
    name: medicine.name,
    active_ingredient: medicine.activeIngredient || null,
    dosage: medicine.dosage || null,
    form: medicine.form || null,
    category: medicine.category || null,
    notes: `[owner:${medicine.owner || 'shared'}] ${medicine.note || medicine.usageSummary || ''}`.trim(),
    photo: medicine.photo || null,
    usage_tags: medicine.usageTags || [],
    intake_method: medicine.intakeMethod || null,
    dosing_schedule_text: medicine.dosingScheduleText || null,
    dosing_schedule_source: medicine.dosingScheduleSource || null,
    dosing_schedule_source_url: medicine.dosingScheduleSourceUrl || null,
    created_by: userId,
  }).select('id').single();
  if (error) throw error;

  const { error: batchError } = await client.from('medicine_batches').insert({
    medicine_id: row.id,
    household_id: householdId,
    quantity: Number(medicine.amount || 0),
    low_stock_threshold: medicine.threshold === undefined || medicine.threshold === '' ? null : Number(medicine.threshold),
    unit: medicine.unit || 'шт.',
    expires_on: medicine.expiry || null,
    storage_location: medicine.storage || null,
    created_by: userId,
  });
  if (batchError) {
    await client.from('medicines').update({ deleted_at: new Date().toISOString() }).eq('id', row.id);
    throw batchError;
  }

  return { ...medicine, id: row.id };
}

export async function updateCloudMedicine(medicine: Medicine): Promise<void> {
  const client = requireClient();
  const { error } = await client.from('medicines').update({
    name: medicine.name,
    active_ingredient: medicine.activeIngredient || null,
    dosage: medicine.dosage || null,
    form: medicine.form || null,
    notes: `[owner:${medicine.owner || 'shared'}] ${medicine.note || medicine.usageSummary || ''}`.trim(),
    photo: medicine.photo || null,
    usage_tags: medicine.usageTags || [],
    intake_method: medicine.intakeMethod || null,
    dosing_schedule_text: medicine.dosingScheduleText || null,
    dosing_schedule_source: medicine.dosingScheduleSource || null,
    dosing_schedule_source_url: medicine.dosingScheduleSourceUrl || null,
  }).eq('id', medicine.id);
  if (error) throw error;

  const { error: batchError } = await client.from('medicine_batches').update({
    quantity: Number(medicine.amount || 0),
    low_stock_threshold: medicine.threshold === undefined || medicine.threshold === '' ? null : Number(medicine.threshold),
    unit: medicine.unit || 'шт.',
    expires_on: medicine.expiry || null,
    storage_location: medicine.storage || null,
  }).eq('medicine_id', medicine.id).is('deleted_at', null);
  if (batchError) throw batchError;
}

export async function softDeleteCloudMedicine(id: string): Promise<void> {
  const client = requireClient();
  const deletedAt = new Date().toISOString();
  const { error } = await client.from('medicines').update({ deleted_at: deletedAt }).eq('id', id);
  if (error) throw error;
  const { error: batchError } = await client.from('medicine_batches').update({ deleted_at: deletedAt }).eq('medicine_id', id);
  if (batchError) throw batchError;
}
